import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { List, OrderedMap, Map } from 'immutable';
import { canUseDOM } from 'exenv';

import Seller from 'models/seller';
import UserModel from 'models/user';
import Brand from 'models/brand';

import ValidationSection from 'components/parking-passes/validation-section';
import InfoSection from 'components/parking-passes/info-section';
import ParkingInstructions from 'components/parking-passes/parking-instructions';
import Loading from 'containers/common/loading';
import { PAGEVIEW_EVENT } from 'lib/analytics/events/index';
import { pageProps, urlProps } from 'lib/analytics/page-properties';
import { PARKING_PASS_APP } from 'lib/app-names';

import setModalState from 'action-creators/modal/set-modal-state';
import addMessage from 'action-creators/messaging/add-message';
import trackEventCreator from 'action-creators/analytics/track-event';
import addVehicleCreator from 'action-creators/bookings/add-vehicle';
import openGateCreator from 'action-creators/parking-pass/open-gate';
import cleanupParkingPassesState from 'action-creators/parking-pass/cleanup-parking-passes-state';

import { getSelectedBookings, getSelectedParkingPasses } from 'selectors';

const propTypes = {
  bookings: PropTypes.instanceOf(List).isRequired,
  parkingPasses: PropTypes.instanceOf(List).isRequired,
  locations: PropTypes.instanceOf(OrderedMap).isRequired,
  vehicles: PropTypes.instanceOf(OrderedMap),
  sellers: PropTypes.instanceOf(Map).isRequired,
  tickets: PropTypes.instanceOf(List),
  events: PropTypes.instanceOf(OrderedMap),
  venues: PropTypes.instanceOf(Map),
  user: PropTypes.instanceOf(UserModel).isRequired,
  addVehicle: PropTypes.func.isRequired,
  openGate: PropTypes.func.isRequired,
  changeModalState: PropTypes.func.isRequired,
  addMessage: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  locale: PropTypes.string.isRequired,
  cleanupParkingPassesState: PropTypes.func.isRequired,
  usejs: PropTypes.bool.isRequired,
  appContext: PropTypes.string.isRequired,
};

const defaultProps = {
  events: new OrderedMap(),
  venues: new Map(),
  vehicles: new OrderedMap(),
};

class ParkingPasses extends Component {
  componentWillMount() {
    if (canUseDOM) {
      const pageProperties = pageProps({ app: PARKING_PASS_APP });
      this.props.trackEvent({
        ...PAGEVIEW_EVENT,
        ...pageProperties,
        name: pageProperties.pageName,
        properties: {
          ...urlProps(),
          PassIDs: this.props.parkingPasses.map(p => p.id),
        },
      });
    }
  }

  componentWillUnmount() {
    this.props.cleanupParkingPassesState();
  }

  renderParkingPass(parkingPass, pageIndex, booking) {
    const {
      locations,
      sellers,
      events,
      venues,
      vehicles,
      tickets,
      user,
      addVehicle,
      openGate,
      changeModalState,
      addMessage,
      brand,
      locale,
      usejs,
      appContext,
      trackEvent,
    } = this.props;

    const event = parkingPass.getEvent(events);
    const location = parkingPass.getLocation(locations);
    const seller = parkingPass.getSeller(sellers) || new Seller();
    const venue = parkingPass.getVenue(venues, events);
    const ticket = booking.getTicket(tickets);

    return (
      <div
        className={`parking-pass ${parkingPass.isExpired ? 'expired' : null}`}
        key={parkingPass.id}
      >
        <ValidationSection
          booking={booking}
          parkingPass={parkingPass}
          vehicles={vehicles}
          ticket={ticket}
          user={user}
          addVehicle={addVehicle}
          openGate={openGate}
          changeModalState={changeModalState}
          addMessage={addMessage}
          pageIndex={pageIndex}
          brand={brand}
          location={location}
          usejs={usejs}
          appContext={appContext}
        />
        <InfoSection
          booking={booking}
          parkingPass={parkingPass}
          location={location}
          seller={seller}
          event={event}
          venue={venue}
          locale={locale}
          app={PARKING_PASS_APP}
          appContext={appContext}
          trackEvent={trackEvent}
        />
        <ParkingInstructions
          parkingPass={parkingPass}
          location={location}
          venue={venue}
          brand={brand}
        />
      </div>
    );
  }

  render() {
    const { bookings, parkingPasses } = this.props;
    const totalPages = parkingPasses.size;

    let parkingPassElements;

    if (totalPages) {
      parkingPassElements = parkingPasses.map(
        (parkingPass, index) => {
          // The index here is zero indexed but we want the page number to start at 1, so we add 1 to the currentPage
          const pageIndex = new Map([['currentPage', index + 1], ['totalPages', totalPages]]);
          const booking = bookings.find(b => b.get('id') === parkingPass.id);
          return this.renderParkingPass(parkingPass, pageIndex, booking);
        },
      );
    } else {
      parkingPassElements = <Loading />;
    }

    return (
      <div className="parking-passes">
        {parkingPassElements}
      </div>
    );
  }
}

ParkingPasses.propTypes = propTypes;
ParkingPasses.defaultProps = defaultProps;

const mapStateToProps = (state, props) => {
  const {
    locations,
    sellers,
    events,
    venues,
    tickets,
  } = state.bookings;

  const {
    user,
    vehicles,
  } = state.account;

  const { brand } = state.brand;
  const { appContext, locale, usejs } = state.app;

  return {
    bookings: getSelectedBookings(state, props),
    parkingPasses: getSelectedParkingPasses(state, props),
    locations,
    sellers,
    events,
    venues,
    vehicles,
    tickets,
    user,
    brand,
    locale,
    usejs,
    appContext,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    addVehicle: addVehicleCreator,
    openGate: openGateCreator,
    changeModalState: setModalState,
    addMessage,
    trackEvent: trackEventCreator,
    cleanupParkingPassesState,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(ParkingPasses);
